import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, fetchSmsMediaBlob } from '../api';
import type { JobCallbackRow } from '../api';
import { prepareImage, isHeic, heicToJpegBlob, fileToBase64 } from './SmsChat';

type PhotoKind = 'before' | 'after' | 'callback' | 'issue';

/** Format a stored ISO timestamp for Brisbane, short and readable. */
function fmtStamp(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Brisbane' });
  } catch { return ''; }
}
const pad2 = (n: number) => String(n).padStart(2, '0');
/** ISO/Date → value for a <input type="datetime-local"> (local time). */
function toLocalInput(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
/** Default fix slot: tomorrow at 8am local. */
function defaultFixTime(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(8, 0, 0, 0);
  return toLocalInput(d);
}

/** Human duration between two ISO timestamps, e.g. "2h 15m". */
function fmtDuration(a: string | null | undefined, b: string | null | undefined): string {
  if (!a || !b) return '';
  const ms = new Date(b).getTime() - new Date(a).getTime();
  if (!(ms > 0)) return '';
  const mins = Math.round(ms / 60000);
  const h = Math.floor(mins / 60); const m = mins % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
}

/** One stored job photo or video, fetched through the authed proxy and shown as
 *  a thumb. Before/after clips from a phone arrive as video/* (often .mov) — those
 *  must render as <video>, not <img>, or they show blank and "can't play". A
 *  download link is always offered so media is never lost even if a codec (e.g.
 *  iPhone HEVC .mov) won't decode inline. */
function Thumb({ eventId, id, onRemove }: { eventId: string; id: number; onRemove?: () => void }) {
  const [src, setSrc] = useState<string | null>(null);
  const [isVideo, setIsVideo] = useState(false);
  useEffect(() => {
    let active = true; let obj: string | null = null;
    fetchSmsMediaBlob(`/calendar/jobs/${encodeURIComponent(eventId)}/photos/${id}/content`)
      .then(async (blob) => {
        const video = blob.type.startsWith('video/');
        // iPhone HEIC photos don't render in <img> — convert to JPEG for display.
        const b = (!video && isHeic(blob.type)) ? await heicToJpegBlob(blob).catch(() => blob) : blob;
        if (!active) return;
        obj = URL.createObjectURL(b);
        setSrc(obj); setIsVideo(video);
      })
      .catch(() => {});
    return () => { active = false; if (obj) URL.revokeObjectURL(obj); };
  }, [eventId, id]);
  const box: React.CSSProperties = { width: 84, height: 84, borderRadius: 6, border: '1px solid var(--color-border)', display: 'block', objectFit: 'cover' };
  return (
    <div style={{ position: 'relative' }}>
      {!src ? <div style={{ ...box, background: 'var(--color-background)' }} />
        : isVideo
          ? <video src={src} controls playsInline preload="metadata" style={{ ...box, background: '#000' }} />
          : <a href={src} target="_blank" rel="noreferrer"><img src={src} alt="job" style={box} /></a>}
      {src && <a href={src} download title="Download" style={{ position: 'absolute', bottom: -6, left: -6, width: 20, height: 20, borderRadius: '50%', background: 'var(--color-text)', color: 'var(--color-surface)', textAlign: 'center', fontSize: 11, lineHeight: '20px', textDecoration: 'none' }}>⬇</a>}
      {onRemove && <button onClick={onRemove} title="Remove" style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', border: 'none', background: 'var(--color-error)', color: '#fff', cursor: 'pointer', fontSize: 12, lineHeight: '20px', padding: 0 }}>✕</button>}
    </div>
  );
}

const section: React.CSSProperties = { marginTop: 'var(--spacing-3)', padding: 'var(--spacing-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'var(--color-surface-elevated)' };

/** A photos section for one kind (before / after / callback). Uploads one photo
 *  per request (sequential) so big batches from a phone don't fail as one blob. */
export function JobPhotoGroup({ eventId, kind, title }: { eventId: string; kind: PhotoKind; title: string }) {
  const qc = useQueryClient();
  const key = ['job-state', eventId];
  const { data } = useQuery({ queryKey: key, queryFn: () => api.getJobState(eventId), enabled: Boolean(eventId) });
  const photos = (data?.photos ?? []).filter((p) => (p.kind || 'after') === kind);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const set = (d: any) => qc.setQueryData(key, (prev: any) => ({ ...prev, ...d }));

  const upload = async (list: File[]) => {
    if (!list.length) return;
    list = list.slice(0, 30);
    setBusy(true); setErr(null); setProgress({ done: 0, total: list.length });
    let failed = 0;
    for (let i = 0; i < list.length; i++) {
      try {
        const f = list[i];
        // Videos (before/after clips) are sent as-is — prepareImage only handles stills.
        const p = f.type.startsWith('video/') ? { base64: await fileToBase64(f), mime: f.type || 'video/mp4' } : await prepareImage(f);
        const d = await api.uploadJobPhotos(eventId, [{ base64: p.base64, mime: p.mime }], kind);
        set({ photos: d.photos });
      } catch { failed++; }
      setProgress({ done: i + 1, total: list.length });
    }
    setBusy(false); setProgress(null);
    // Before/after photos auto-start the job server-side — refresh timing display.
    if ((kind === 'before' || kind === 'after')) qc.invalidateQueries({ queryKey: key });
    if (failed) setErr(`${failed} photo${failed > 1 ? 's' : ''} didn’t upload — tap Add again to retry.`);
  };
  const remove = useMutation({ mutationFn: (id: number) => api.removeJobPhoto(eventId, id), onSuccess: (d) => set(d) });

  return (
    <div style={section}>
      <strong style={{ fontSize: 'var(--font-size-sm)' }}>{title}</strong>
      <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap', margin: 'var(--spacing-2) 0' }}>
        {photos.map((p) => <Thumb key={p.id} eventId={eventId} id={p.id} onRemove={() => remove.mutate(p.id)} />)}
        {photos.length === 0 && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>No photos yet.</span>}
      </div>
      {/* No `capture` attribute: on iOS Safari, capture + multiple opens the
          camera directly and then drops the photo ("no files selected" after
          Use Photo). Without it iOS offers Take Photo / Photo Library / Choose
          File, which hands the file back reliably and also lets the contractor
          pick shots they already took with the camera app. */}
      <input type="file" accept="image/*,video/*" multiple disabled={busy} onChange={(e) => { const files = Array.from(e.target.files ?? []); e.currentTarget.value = ''; void upload(files); }} style={{ width: '100%', fontSize: 'var(--font-size-sm)' }} />
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 4 }}>Take a photo, or choose from your photo library.</div>
      {busy && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 4 }}>Uploading{progress ? ` ${Math.min(progress.done + 1, progress.total)} of ${progress.total}` : ''}… (one at a time)</div>}
      {err && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-error)', marginTop: 4 }}>{err}</div>}
    </div>
  );
}

/** Timing summary (admin only). The job auto-starts when the first scope item or
 *  photo is added, so there's no "Start" button; this just shows the office the
 *  start/finish times. Subcontractors don't see timing at all. */
export function JobStart({ eventId, isAdmin }: { eventId: string; isAdmin: boolean }) {
  const { data } = useQuery({ queryKey: ['job-state', eventId], queryFn: () => api.getJobState(eventId), enabled: Boolean(eventId) });
  const startedAt = data?.startedAt ?? null;
  const finishedAt = data?.finishedAt ?? null;
  if (!isAdmin || (!startedAt && !finishedAt)) return null;
  return (
    <div style={section}>
      <div style={{ fontSize: 'var(--font-size-sm)', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {startedAt && <span>🕒 <strong>Started</strong> {fmtStamp(startedAt)}{data?.startedBy ? ` · ${data.startedBy}` : ''}</span>}
        {finishedAt && (
          <span style={{ color: 'var(--color-accent)' }}>✅ <strong>Done</strong> {fmtStamp(finishedAt)}{data?.finishedBy ? ` · ${data.finishedBy}` : ''}{fmtDuration(startedAt, finishedAt) ? ` · ${fmtDuration(startedAt, finishedAt)} on site` : ''}</span>
        )}
      </div>
    </div>
  );
}

/** Admin note on a job, shown to the contractor. Admin sees an editable box;
 *  contractors see the note read-only (and nothing if there isn't one). */
export function JobNotes({ eventId, isAdmin }: { eventId: string; isAdmin: boolean }) {
  const qc = useQueryClient();
  const key = ['job-state', eventId];
  const { data } = useQuery({ queryKey: key, queryFn: () => api.getJobState(eventId), enabled: Boolean(eventId) });
  const note = data?.note ?? '';
  const [draft, setDraft] = useState<string | null>(null); // null = mirror saved note
  const value = draft ?? note;
  const save = useMutation({
    mutationFn: (v: string) => api.setJobNote(eventId, v),
    onSuccess: (d: any) => { qc.setQueryData(key, (prev: any) => ({ ...prev, ...d })); setDraft(null); },
  });

  if (!isAdmin) {
    if (!note.trim()) return null;
    return (
      <div style={section}>
        <strong style={{ fontSize: 'var(--font-size-sm)' }}>Notes</strong>
        <div style={{ whiteSpace: 'pre-wrap', marginTop: 4, fontSize: 'var(--font-size-sm)' }}>{note}</div>
      </div>
    );
  }
  return (
    <div style={section}>
      <strong style={{ fontSize: 'var(--font-size-sm)' }}>Notes</strong>
      <textarea
        value={value}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Notes for the contractor…"
        rows={3}
        style={{ width: '100%', marginTop: 'var(--spacing-2)', padding: 'var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'var(--color-background)', color: 'var(--color-text)', boxSizing: 'border-box', fontSize: 'var(--font-size-sm)', resize: 'vertical' }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--spacing-1)' }}>
        <button
          onClick={() => save.mutate(value)}
          disabled={save.isPending || value === note}
          style={{ padding: '4px 12px', border: '1px solid var(--color-accent)', borderRadius: 'var(--radius-default)', background: 'var(--color-accent)', color: 'var(--color-text-inverse)', fontWeight: 600, cursor: value === note ? 'default' : 'pointer', fontSize: 'var(--font-size-xs)', opacity: value === note ? 0.6 : 1 }}
        >
          {save.isPending ? 'Saving…' : 'Save notes'}
        </button>
      </div>
    </div>
  );
}

/** Free-text note the field team writes about what happened on the job / extra
 *  work spotted (stains, graffiti…). Everyone on the job can edit it. */
export function JobCompletionNote({ eventId }: { eventId: string }) {
  const qc = useQueryClient();
  const key = ['job-state', eventId];
  const { data } = useQuery({ queryKey: key, queryFn: () => api.getJobState(eventId), enabled: Boolean(eventId) });
  const note = data?.completionNote ?? '';
  const [draft, setDraft] = useState<string | null>(null); // null = mirror saved note
  const value = draft ?? note;
  const save = useMutation({
    mutationFn: (v: string) => api.setJobCompletionNote(eventId, v),
    onSuccess: (d: any) => { qc.setQueryData(key, (prev: any) => ({ ...prev, ...d })); setDraft(null); },
  });
  return (
    <div style={section}>
      <strong style={{ fontSize: 'var(--font-size-sm)' }}>What happened on the job</strong>
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', margin: '2px 0 var(--spacing-1)' }}>
        Notes about the job and any extra work spotted (stains, graffiti, etc.).
      </div>
      <textarea
        value={value}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="e.g. Heavy oil stain near bay 3 — could be treated as extra work…"
        rows={4}
        style={{ width: '100%', marginTop: 'var(--spacing-1)', padding: 'var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'var(--color-background)', color: 'var(--color-text)', boxSizing: 'border-box', fontSize: 'var(--font-size-sm)', resize: 'vertical' }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--spacing-1)' }}>
        <button
          onClick={() => save.mutate(value)}
          disabled={save.isPending || value === note}
          style={{ padding: '4px 12px', border: '1px solid var(--color-accent)', borderRadius: 'var(--radius-default)', background: 'var(--color-accent)', color: 'var(--color-text-inverse)', fontWeight: 600, cursor: value === note ? 'default' : 'pointer', fontSize: 'var(--font-size-xs)', opacity: value === note ? 0.6 : 1 }}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

/** A non-image attachment (e.g. PDF) shown as a download link with its filename. */
function DocLink({ eventId, id, filename, onRemove }: { eventId: string; id: number; filename: string | null; onRemove?: () => void }) {
  const [href, setHref] = useState<string | null>(null);
  useEffect(() => {
    let active = true; let obj: string | null = null;
    fetchSmsMediaBlob(`/calendar/jobs/${encodeURIComponent(eventId)}/photos/${id}/content`)
      .then((b) => { if (!active) return; obj = URL.createObjectURL(b); setHref(obj); })
      .catch(() => {});
    return () => { active = false; if (obj) URL.revokeObjectURL(obj); };
  }, [eventId, id]);
  const name = filename || 'Document';
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid var(--color-border)', borderRadius: 6, padding: '6px 10px', background: 'var(--color-background)', maxWidth: 240 }}>
      <a href={href || undefined} download={name} target="_blank" rel="noreferrer" style={{ color: 'var(--color-accent)', fontWeight: 600, fontSize: 'var(--font-size-sm)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📄 {name}</a>
      {onRemove && <button onClick={onRemove} title="Remove" style={{ background: 'none', border: 'none', color: 'var(--color-error)', cursor: 'pointer', fontSize: 12, padding: 0, flexShrink: 0 }}>✕</button>}
    </div>
  );
}

/** Admin attachments for a job (reference photos or documents), shown under the
 *  Notes. Admins upload/remove; contractors view/download read-only. */
export function JobAttachments({ eventId, isAdmin }: { eventId: string; isAdmin: boolean }) {
  const qc = useQueryClient();
  const key = ['job-state', eventId];
  const { data } = useQuery({ queryKey: key, queryFn: () => api.getJobState(eventId), enabled: Boolean(eventId) });
  const items = (data?.photos ?? []).filter((p) => (p.kind || '') === 'note');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (d: any) => qc.setQueryData(key, (prev: any) => ({ ...prev, ...d }));
  const remove = useMutation({ mutationFn: (id: number) => api.removeJobPhoto(eventId, id), onSuccess: (d) => set(d) });

  const upload = async (files: File[]) => {
    if (!files.length) return;
    setBusy(true); setErr(null);
    let failed = 0;
    for (const f of files.slice(0, 20)) {
      try {
        const isImg = (f.type || '').startsWith('image/') || isHeic(f.type, f.name);
        let body: { base64: string; mime: string; filename?: string };
        if (isImg) { const p = await prepareImage(f); body = { base64: p.base64, mime: p.mime, filename: f.name }; }
        else { body = { base64: await fileToBase64(f), mime: f.type || 'application/octet-stream', filename: f.name }; }
        const d = await api.uploadJobPhotos(eventId, [body], 'note');
        set({ photos: d.photos });
      } catch { failed++; }
    }
    setBusy(false);
    if (failed) setErr(`${failed} file${failed > 1 ? 's' : ''} didn’t upload — try again.`);
  };

  if (!isAdmin && items.length === 0) return null;

  return (
    <div style={section}>
      <strong style={{ fontSize: 'var(--font-size-sm)' }}>📎 Attachments</strong>
      <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap', margin: 'var(--spacing-2) 0' }}>
        {items.map((p) => (
          (p.contentType || '').startsWith('image/') || isHeic(p.contentType)
            ? <Thumb key={p.id} eventId={eventId} id={p.id} onRemove={isAdmin ? () => remove.mutate(p.id) : undefined} />
            : <DocLink key={p.id} eventId={eventId} id={p.id} filename={p.filename} onRemove={isAdmin ? () => remove.mutate(p.id) : undefined} />
        ))}
        {items.length === 0 && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>No attachments yet.</span>}
      </div>
      {isAdmin && (
        <>
          <input type="file" accept="image/*,application/pdf,.pdf,.doc,.docx" multiple disabled={busy}
            onChange={(e) => { const files = Array.from(e.target.files ?? []); e.currentTarget.value = ''; void upload(files); }}
            style={{ width: '100%', fontSize: 'var(--font-size-sm)' }} />
          {busy && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 4 }}>Uploading…</div>}
          {err && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-error)', marginTop: 4 }}>{err}</div>}
        </>
      )}
    </div>
  );
}

/** "Job done" action (bottom of the job module): stamps the finish time and
 *  flags the job ready to invoice (the office invoices from here). */
/** If this job's photos/scope were orphaned by an earlier re-book (event was
 *  re-created), offer to re-link them to the current event. Hidden when there's
 *  nothing to recover. */
export function JobRecover({ eventId }: { eventId: string }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['job-orphaned', eventId], queryFn: () => api.getOrphanedJobData(eventId), enabled: Boolean(eventId) });
  const recover = useMutation({
    mutationFn: () => api.recoverJobData(eventId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['job-state', eventId] });
      qc.invalidateQueries({ queryKey: ['job-scope', eventId, 'scope'] });
      qc.invalidateQueries({ queryKey: ['job-checklists', eventId] });
      qc.invalidateQueries({ queryKey: ['job-orphaned', eventId] });
    },
  });
  const photos = data?.photos ?? 0;
  const scope = data?.scope ?? 0;
  if (!photos && !scope && !recover.isSuccess) return null;
  return (
    <div style={{ marginTop: 'var(--spacing-3)', background: 'rgba(254,197,83,0.16)', border: '2px solid #FEC553', borderRadius: 'var(--radius-default)', padding: 'var(--spacing-3)' }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>♻️ Recover earlier work</div>
      {(photos > 0 || scope > 0) && (
        <div style={{ fontSize: 'var(--font-size-sm)', marginBottom: 8 }}>
          {photos} photo{photos === 1 ? '' : 's'} and {scope} scope item{scope === 1 ? '' : 's'} from an earlier booking of this job aren’t showing (it was re-booked). Re-link them to this job?
        </div>
      )}
      {photos > 0 || scope > 0 ? (
        <button onClick={() => recover.mutate()} disabled={recover.isPending}
          style={{ padding: '6px 12px', border: '1px solid var(--color-accent)', borderRadius: 'var(--radius-default)', background: 'var(--color-accent)', color: 'var(--color-text-inverse)', fontWeight: 600, cursor: 'pointer' }}>
          {recover.isPending ? 'Recovering…' : 'Recover photos & scope'}
        </button>
      ) : null}
      {recover.isSuccess && <div style={{ marginTop: 6, color: 'var(--color-success, #2e7d32)', fontSize: 'var(--font-size-sm)' }}>✓ Recovered {recover.data.photos} photo(s) and {recover.data.scope} scope item(s).</div>}
      {recover.isError && <div style={{ marginTop: 6, color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' }}>{(recover.error as Error).message}</div>}
    </div>
  );
}

/** Pre-start checklist — a noticeable amber panel shown under the notes. Each
 *  item disappears once ticked (tick-to-dismiss); state is saved per job so it
 *  stays dismissed when the job is reopened. Once all are ticked, it hides. */
export function JobPrestartChecklist({ eventId }: { eventId: string }) {
  const qc = useQueryClient();
  const key = ['job-checklists', eventId];
  const { data } = useQuery({ queryKey: key, queryFn: () => api.getJobChecklists(eventId), enabled: Boolean(eventId) });
  const tick = useMutation({
    mutationFn: (v: { phase: 'firstjob' | 'prestart'; itemKey: string }) => api.setJobChecklistItem(eventId, v.phase, v.itemKey, true),
    onSuccess: (d) => qc.setQueryData(key, d),
  });
  // "Before first job" equipment checks only surface on the first job of the day.
  const firstjob = data?.isFirstJobOfDay ? (data?.firstjob ?? []).filter((i) => !i.checked) : [];
  const prestart = (data?.prestart ?? []).filter((i) => !i.checked);
  if (!firstjob.length && !prestart.length) return null;
  const item = (phase: 'firstjob' | 'prestart', it: { key: string; label: string }) => (
    <label key={`${phase}:${it.key}`} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>
      <input type="checkbox" checked={false} disabled={tick.isPending} onChange={() => tick.mutate({ phase, itemKey: it.key })} style={{ marginTop: 3 }} />
      <span>{it.label}</span>
    </label>
  );
  return (
    <div style={{ marginTop: 'var(--spacing-3)', background: 'rgba(254,197,83,0.16)', border: '2px solid #FEC553', borderRadius: 'var(--radius-default)', padding: 'var(--spacing-3)' }}>
      {firstjob.length > 0 && (
        <>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>🔧 Before your first job today (equipment checks)</div>
          <div style={{ display: 'grid', gap: 8 }}>{firstjob.map((it) => item('firstjob', it))}</div>
        </>
      )}
      {prestart.length > 0 && (
        <>
          <div style={{ fontWeight: 700, margin: firstjob.length ? '12px 0 8px' : '0 0 8px' }}>⚠️ Before you start</div>
          <div style={{ display: 'grid', gap: 8 }}>{prestart.map((it) => item('prestart', it))}</div>
        </>
      )}
    </div>
  );
}

export function JobReady({ eventId, isAdmin }: { eventId: string; isAdmin: boolean }) {
  const qc = useQueryClient();
  const key = ['job-state', eventId];
  const clKey = ['job-checklists', eventId];
  const { data } = useQuery({ queryKey: key, queryFn: () => api.getJobState(eventId), enabled: Boolean(eventId) });
  const { data: cl } = useQuery({ queryKey: clKey, queryFn: () => api.getJobChecklists(eventId), enabled: Boolean(eventId) });
  const done = data?.readyToInvoice ?? false;
  const set = (d: any) => qc.setQueryData(key, (prev: any) => ({ ...prev, ...d }));
  const finish = useMutation({ mutationFn: (v: boolean) => api.finishJob(eventId, v), onSuccess: (d) => set(d) });
  const tick = useMutation({
    mutationFn: (v: { phase: 'preleave' | 'endofday' | 'endofweek'; itemKey: string; checked: boolean }) => api.setJobChecklistItem(eventId, v.phase, v.itemKey, v.checked),
    onSuccess: (d) => qc.setQueryData(clKey, d),
  });

  if (done) {
    return (
      <div style={{ marginTop: 'var(--spacing-3)', display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-accent)' }}>Job done ✅{isAdmin && data?.finishedAt ? ` · ${fmtStamp(data.finishedAt)}` : ''}{isAdmin && data?.finishedBy ? ` · ${data.finishedBy}` : ''}</span>
        <button onClick={() => finish.mutate(false)} disabled={finish.isPending} style={{ padding: '4px 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'transparent', color: 'var(--color-text)', cursor: 'pointer', fontSize: 'var(--font-size-xs)' }}>Undo</button>
      </div>
    );
  }

  const preleave = cl?.preleave ?? [];
  const endofday = cl?.isLastJobOfDay ? (cl?.endofday ?? []) : [];
  const endofweek = cl?.isLastJobOfWeek ? (cl?.endofweek ?? []) : [];
  const allDone = Boolean(cl) && preleave.every((i) => i.checked) && endofday.every((i) => i.checked) && endofweek.every((i) => i.checked);
  const remainingCount = preleave.filter((i) => !i.checked).length + endofday.filter((i) => !i.checked).length + endofweek.filter((i) => !i.checked).length;
  const rowStyle = (checked: boolean): React.CSSProperties => ({ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', fontSize: 'var(--font-size-sm)', textDecoration: checked ? 'line-through' : 'none', opacity: checked ? 0.6 : 1 });
  const row = (phase: 'preleave' | 'endofday' | 'endofweek', it: { key: string; label: string; checked: boolean }) => (
    <label key={`${phase}:${it.key}`} style={rowStyle(it.checked)}>
      <input type="checkbox" checked={it.checked} disabled={tick.isPending} onChange={(e) => tick.mutate({ phase, itemKey: it.key, checked: e.target.checked })} style={{ marginTop: 3 }} />
      <span>{it.label}</span>
    </label>
  );

  return (
    <div style={{ marginTop: 'var(--spacing-3)' }}>
      {cl && (
        <div style={{ background: 'rgba(0,176,151,0.08)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', padding: 'var(--spacing-3)' }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>✅ Before you leave</div>
          <div style={{ display: 'grid', gap: 8 }}>{preleave.map((it) => row('preleave', it))}</div>
          {cl.isLastJobOfDay && (
            <>
              <div style={{ fontWeight: 700, margin: '12px 0 8px' }}>🌙 End of day (last job today)</div>
              <div style={{ display: 'grid', gap: 8 }}>{endofday.map((it) => row('endofday', it))}</div>
            </>
          )}
          {cl.isLastJobOfWeek && (
            <>
              <div style={{ fontWeight: 700, margin: '12px 0 8px' }}>🗓️ Last job of the week</div>
              <div style={{ display: 'grid', gap: 8 }}>{endofweek.map((it) => row('endofweek', it))}</div>
            </>
          )}
        </div>
      )}
      <div style={{ marginTop: 'var(--spacing-2)', display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
        <button
          onClick={() => finish.mutate(true)}
          disabled={!allDone || finish.isPending}
          style={{ padding: 'var(--spacing-2) var(--spacing-3)', border: '1px solid var(--color-accent)', borderRadius: 'var(--radius-default)', background: 'var(--color-accent)', color: 'var(--color-text-inverse)', fontWeight: 600, cursor: allDone ? 'pointer' : 'not-allowed', fontSize: 'var(--font-size-sm)', opacity: allDone ? 1 : 0.5 }}
        >
          Job done ✅
        </button>
        {cl && !allDone && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Tick all {remainingCount} remaining item{remainingCount === 1 ? '' : 's'} to finish.</span>}
        {finish.isError && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-error)' }}>{(finish.error as Error)?.message}</span>}
      </div>
    </div>
  );
}

// ─── Callbacks (return visits) ───────────────────────────────────────────────
const fieldLabel: React.CSSProperties = { display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 4 };
const fieldBox: React.CSSProperties = { width: '100%', minHeight: 40, padding: 'var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'var(--color-background)', color: 'var(--color-text)', boxSizing: 'border-box', fontSize: 'var(--font-size-sm)' };

/** Full-screen modal to create or edit a callback: area, who performed/reviewed,
 *  what was wrong, what the customer said, and defect photos (uploaded one-by-one). */
function CallbackModal({ eventId, callback, onClose, onScheduled }: { eventId: string; callback: JobCallbackRow | null; onClose: () => void; onScheduled?: (localDateTime: string) => void }) {
  const qc = useQueryClient();
  const editing = Boolean(callback);
  const [area, setArea] = useState(callback?.area ?? '');
  const [performedBy, setPerformedBy] = useState(callback?.performedBy ?? '');
  const [reviewedBy, setReviewedBy] = useState(callback?.reviewedBy ?? '');
  const [whatWrong, setWhatWrong] = useState(callback?.whatWrong ?? '');
  const [customerSaid, setCustomerSaid] = useState(callback?.customerSaid ?? '');
  const [scheduledAt, setScheduledAt] = useState<string>(callback?.scheduledAt ? toLocalInput(new Date(callback.scheduledAt)) : defaultFixTime());
  const [durationHours, setDurationHours] = useState<number>(2);
  const [queued, setQueued] = useState<File[]>([]);
  const [existing, setExisting] = useState<{ id: number }[]>([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Staff list for the performed/reviewed dropdowns.
  const { data: staff } = useQuery({ queryKey: ['contractors'], queryFn: () => api.getContractors() });
  const names: string[] = Array.from(new Set(((staff ?? []) as any[]).map((c) => c?.name).filter(Boolean)));
  // Area options come from the job's scope of works.
  const { data: scope } = useQuery({ queryKey: ['job-scope', eventId, 'scope'], queryFn: () => api.getJobScope(eventId, 'scope'), enabled: Boolean(eventId) });
  const scopeAreas: string[] = (scope?.items ?? []).map((i) => i.body).filter(Boolean);

  // Load existing defect photos when editing.
  useEffect(() => {
    if (!callback) return;
    let active = true;
    api.listJobCallbackPhotos(eventId, callback.id).then((d) => { if (active) setExisting(d.photos.map((p) => ({ id: p.id }))); }).catch(() => {});
    return () => { active = false; };
  }, [eventId, callback]);

  const uploadSeq = async (callbackId: number, files: File[]) => {
    let failed = 0;
    setProgress({ done: 0, total: files.length });
    for (let i = 0; i < files.length; i++) {
      try {
        const f = files[i];
        const p = f.type.startsWith('video/') ? { base64: await fileToBase64(f), mime: f.type || 'video/mp4' } : await prepareImage(f);
        await api.uploadJobCallbackPhotos(eventId, callbackId, [{ base64: p.base64, mime: p.mime }]);
      } catch { failed++; }
      setProgress({ done: i + 1, total: files.length });
    }
    setProgress(null);
    return failed;
  };

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const payload = {
        area, performedBy, reviewedBy, whatWrong, customerSaid,
        scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        durationMins: Math.round(durationHours * 60),
      };
      let id = callback?.id;
      if (editing && id) {
        await api.updateJobCallback(eventId, id, payload);
      } else {
        const res = await api.createJobCallback(eventId, payload);
        id = res.callback.id;
      }
      let failed = 0;
      if (id && queued.length) failed = await uploadSeq(id, queued.slice(0, 30));
      await qc.invalidateQueries({ queryKey: ['job-callbacks', eventId] });
      // A scheduled fix adds a calendar event — refresh the calendar so it shows.
      if (scheduledAt) await qc.invalidateQueries({ queryKey: ['calendar-jobs'] });
      if (failed) { setErr(`Saved, but ${failed} photo${failed > 1 ? 's' : ''} didn’t upload. Reopen this callback to retry.`); setBusy(false); return; }
      onClose();
      // Jump the calendar to the booked-fix week so the new appointment is visible.
      if (scheduledAt) onScheduled?.(scheduledAt);
    } catch (e: any) { setErr(e?.message || 'Could not save the callback.'); setBusy(false); }
  };

  const removeExisting = async (id: number) => {
    try { await api.removeJobPhoto(eventId, id); setExisting((prev) => prev.filter((p) => p.id !== id)); } catch { /* ignore */ }
  };

  return (
    <div role="dialog" aria-modal="true" onClick={busy ? undefined : onClose}
      style={{ position: 'fixed', inset: 0, background: 'var(--color-surface)', zIndex: 1100 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--color-surface)', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
        <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-3)', padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-border)' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg, 20px)' }}>🔧 {editing ? 'Callback' : 'New callback'}</h2>
          <button onClick={onClose} disabled={busy} aria-label="Close" style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%', border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text)', fontSize: 22, lineHeight: 1, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: 'var(--spacing-4)' }}>
          <div style={{ maxWidth: 720, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
            <div>
              <label style={fieldLabel}>Area</label>
              {scopeAreas.length > 0 ? (
                <select value={area} onChange={(e) => setArea(e.target.value)} style={fieldBox}>
                  <option value="">Select from scope…</option>
                  {scopeAreas.map((a) => <option key={a} value={a}>{a}</option>)}
                  {area && !scopeAreas.includes(area) && <option value={area}>{area}</option>}
                </select>
              ) : (
                <>
                  <input value={area} onChange={(e) => setArea(e.target.value)} placeholder="e.g. Windows, Driveway, Front entry" style={fieldBox} />
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 4 }}>Tip: add the work to the job’s Scope of works and it’ll appear here to pick from.</div>
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: 'var(--spacing-3)', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <label style={fieldLabel}>Performed by</label>
                <select value={performedBy} onChange={(e) => setPerformedBy(e.target.value)} style={fieldBox}>
                  <option value="">Select staff…</option>
                  {names.map((n) => <option key={n} value={n}>{n}</option>)}
                  {performedBy && !names.includes(performedBy) && <option value={performedBy}>{performedBy}</option>}
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <label style={fieldLabel}>Reviewed by</label>
                <select value={reviewedBy} onChange={(e) => setReviewedBy(e.target.value)} style={fieldBox}>
                  <option value="">Select staff…</option>
                  {names.map((n) => <option key={n} value={n}>{n}</option>)}
                  {reviewedBy && !names.includes(reviewedBy) && <option value={reviewedBy}>{reviewedBy}</option>}
                </select>
              </div>
            </div>
            <div>
              <label style={fieldLabel}>What was wrong</label>
              <textarea value={whatWrong} onChange={(e) => setWhatWrong(e.target.value)} rows={3} placeholder="Describe the defect / what went wrong" style={{ ...fieldBox, resize: 'vertical' }} />
            </div>
            <div>
              <label style={fieldLabel}>What the customer said</label>
              <textarea value={customerSaid} onChange={(e) => setCustomerSaid(e.target.value)} rows={3} placeholder="In the customer's words" style={{ ...fieldBox, resize: 'vertical' }} />
            </div>
            <div>
              <label style={fieldLabel}>Schedule the fix</label>
              <div style={{ display: 'flex', gap: 'var(--spacing-3)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 2 }}>Return visit date &amp; time</div>
                  <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} style={fieldBox} />
                </div>
                <div style={{ width: 110 }}>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 2 }}>Hours</div>
                  <input type="number" min={0.5} max={12} step={0.5} value={durationHours} onChange={(e) => setDurationHours(Math.min(12, Math.max(0.5, Number(e.target.value) || 2)))} style={fieldBox} />
                </div>
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 4, display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap', alignItems: 'center' }}>
                <span>{scheduledAt ? '📅 A callback appointment will be added to the calendar.' : 'No date set, so no appointment will be created.'}</span>
                {scheduledAt && <button type="button" onClick={() => setScheduledAt('')} style={{ background: 'none', border: 'none', color: 'var(--color-accent)', cursor: 'pointer', padding: 0, fontSize: 'var(--font-size-xs)' }}>Clear</button>}
              </div>
            </div>
            <div style={section}>
              <strong style={{ fontSize: 'var(--font-size-sm)' }}>📷 Defect photos</strong>
              <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap', margin: 'var(--spacing-2) 0' }}>
                {existing.map((p) => <Thumb key={p.id} eventId={eventId} id={p.id} onRemove={() => removeExisting(p.id)} />)}
                {existing.length === 0 && queued.length === 0 && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>No photos yet.</span>}
                {queued.length > 0 && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', alignSelf: 'center' }}>{queued.length} new photo{queued.length > 1 ? 's' : ''} ready to upload on save.</span>}
              </div>
              {/* No `capture` (see JobPhotoGroup): iOS drops the shot when capture + multiple are combined. */}
              <input type="file" accept="image/*,video/*" multiple disabled={busy}
                onChange={(e) => { const files = Array.from(e.target.files ?? []); e.currentTarget.value = ''; setQueued((prev) => [...prev, ...files]); }}
                style={{ width: '100%', fontSize: 'var(--font-size-sm)' }} />
              {progress && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 4 }}>Uploading {Math.min(progress.done + 1, progress.total)} of {progress.total}… (one at a time)</div>}
            </div>
            {err && <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-error)' }}>{err}</div>}
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
              <button onClick={onClose} disabled={busy} style={{ padding: 'var(--spacing-2) var(--spacing-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'transparent', color: 'var(--color-text)', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>Cancel</button>
              <button onClick={save} disabled={busy} style={{ padding: 'var(--spacing-2) var(--spacing-4)', border: 'none', borderRadius: 'var(--radius-default)', background: 'var(--color-accent)', color: 'var(--color-text-inverse)', fontWeight: 700, cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>
                {busy ? 'Saving…' : editing ? 'Save callback' : 'Create callback'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Admin "Call Back" section: lists a job's callbacks and opens the callback
 *  modal. Creating a callback is only enabled once the job is done/completed. */
export function JobCallback({ eventId, completed, onScheduled }: { eventId: string; completed: boolean; onScheduled?: (localDateTime: string) => void }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['job-callbacks', eventId], queryFn: () => api.listJobCallbacks(eventId), enabled: Boolean(eventId) });
  const { data: state } = useQuery({ queryKey: ['job-state', eventId], queryFn: () => api.getJobState(eventId), enabled: Boolean(eventId) });
  const canCreate = completed || (state?.readyToInvoice ?? false) || state?.finishedAt != null;
  const callbacks = data?.callbacks ?? [];
  const [editing, setEditing] = useState<JobCallbackRow | null>(null);
  const [creating, setCreating] = useState(false);
  const remove = useMutation({ mutationFn: (id: number) => api.removeJobCallback(eventId, id), onSuccess: (d) => qc.setQueryData(['job-callbacks', eventId], d) });
  // Resolving a callback clears the "open callback" gate so the job can be thumbed up.
  const resolve = useMutation({
    mutationFn: (id: number) => api.resolveJobCallback(eventId, id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['job-callbacks', eventId] }); qc.invalidateQueries({ queryKey: ['job-quality', eventId] }); },
  });

  return (
    <div style={{ ...section, borderColor: 'var(--color-accent)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 'var(--font-size-sm)' }}>🔧 Call Back</strong>
        {canCreate ? (
          <button onClick={() => setCreating(true)} style={{ padding: '6px 12px', border: '1px solid var(--color-accent)', borderRadius: 'var(--radius-default)', background: 'transparent', color: 'var(--color-accent)', fontWeight: 600, cursor: 'pointer', fontSize: 'var(--font-size-xs)' }}>➕ New callback</button>
        ) : (
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Available once the job is marked done.</span>
        )}
      </div>
      <div style={{ marginTop: 'var(--spacing-2)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
        {callbacks.length === 0 && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>No callbacks logged.</span>}
        {callbacks.map((c) => (
          <div key={c.id} style={{ padding: 'var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'var(--color-background)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-2)' }}>
              <strong style={{ fontSize: 'var(--font-size-sm)' }}>{c.area || 'Callback'}</strong>
              <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
                {c.resolvedAt ? (
                  <span style={{ color: 'var(--color-success, #2e7d32)', fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>✓ Resolved</span>
                ) : (
                  <button onClick={() => resolve.mutate(c.id)} disabled={resolve.isPending} style={{ background: 'none', border: 'none', color: 'var(--color-success, #2e7d32)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', padding: 0, fontWeight: 600 }}>Mark resolved</button>
                )}
                <button onClick={() => setEditing(c)} style={{ background: 'none', border: 'none', color: 'var(--color-accent)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', padding: 0 }}>Open</button>
                <button onClick={() => { if (confirm('Delete this callback and its photos?')) remove.mutate(c.id); }} style={{ background: 'none', border: 'none', color: 'var(--color-error)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', padding: 0 }}>Delete</button>
              </div>
            </div>
            {c.whatWrong && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>{c.whatWrong}</div>}
            {c.scheduledAt && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-accent)', marginTop: 2, fontWeight: 600 }}>📅 Fix booked {fmtStamp(c.scheduledAt)}</div>}
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>
              {[c.performedBy && `By ${c.performedBy}`, c.reviewedBy && `Reviewed ${c.reviewedBy}`, fmtStamp(c.createdAt)].filter(Boolean).join(' · ')}
            </div>
          </div>
        ))}
      </div>
      {(creating || editing) && (
        <CallbackModal eventId={eventId} callback={editing} onClose={() => { setCreating(false); setEditing(null); }} onScheduled={onScheduled} />
      )}
    </div>
  );
}
